import * as React from "react";
import { useState, useEffect } from "react";

import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/joy";
import { FirstPageOutlined } from "@mui/icons-material";

import { SidebarProps } from "../shared/props";

enum SidebarOverride {
    NONE,
    OPEN,
    CLOSE,
}

const styles = {
    container: {
        zIndex: 99,
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid",
        borderColor: "divider",
    },
    headerContainer: {
        paddingTop: 2,
        paddingBottom: 1,
        flexShrink: 0,
    },
    contentContainer: {
        flexGrow: 1,
        overflowY: "auto",
        p: 1,
    },
};

export default function Sidebar({ headerContent, children, collapsed }: SidebarProps) {
    const [sidebarCollapsedOverride, setSidebarCollapsedOverride] = useState(SidebarOverride.NONE);

    // If there's a user override to sidebar open use that, otherwise let the parent component control
    const isSidebarHidden =
        sidebarCollapsedOverride === SidebarOverride.NONE
            ? collapsed
            : sidebarCollapsedOverride === SidebarOverride.CLOSE;
    const toggleSidebar = () =>
        setSidebarCollapsedOverride(isSidebarHidden ? SidebarOverride.OPEN : SidebarOverride.CLOSE);

    const sidebarWidth = isSidebarHidden ? "48px" : "260px";

    useEffect(() => {
        document.documentElement.style.setProperty("--sidebar-width", sidebarWidth);
    }, [sidebarWidth]);

    return (
        <Stack
            sx={{
                ...styles.container,
                ...{ width: sidebarWidth, minWidth: sidebarWidth },
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ ...styles.headerContainer, ...{ px: isSidebarHidden ? 1 : 2 } }}
            >
                {!isSidebarHidden && <Typography level="title-md">{headerContent}</Typography>}

                <Tooltip title={isSidebarHidden ? "Expand sidebar" : "Collapse sidebar"}>
                    <IconButton size="sm" onClick={toggleSidebar}>
                        <FirstPageOutlined
                            sx={{
                                transform: isSidebarHidden ? "rotate(180deg)" : "rotate(0)",
                            }}
                        />
                    </IconButton>
                </Tooltip>
            </Stack>

            {!isSidebarHidden && <Box sx={styles.contentContainer}>{children}</Box>}
        </Stack>
    );
}
